import { useState, useEffect, useCallback } from 'react';

const CART_STORAGE_KEY = 'lootlayer_cart';

const useCart = () => {
  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  // Sync cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  // Sync cart if localStorage changes in another tab/window
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === CART_STORAGE_KEY) {
        setCart(event.newValue ? JSON.parse(event.newValue) : []);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.sku === product.sku);
      if (existingItem) {
        return prevCart.map(item =>
          item.sku === product.sku
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevCart, { ...product, quantity }];
      }
    });
  }, []);

  const removeFromCart = useCallback((sku) => {
    setCart(prevCart => prevCart.filter(item => item.sku !== sku));
  }, []);

  const updateQuantity = useCallback((sku, quantity) => {
    if (quantity < 1) return;
    setCart(prevCart =>
      prevCart.map(item =>
        item.sku === sku ? { ...item, quantity } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  return {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };
};

export default useCart;
